import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Fade,
  Snackbar,
  Alert,
  Typography,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import {
  createCollaborationRequest,
  getByPropertyId,
  getByRealtorAndPropertyId,
} from "../../services/realtorCollaborationRequestsService";
import {
  CollaborationSectionProps,
  CreateRealtorCollaborationRequest,
  RealtorCollaborationRequest,
} from "../../types/realtorCollaborationRequest";
import { useNavigate } from "react-router/dist";
import { 
  Handshake, 
  Share, 
  Percent, 
  Info 
} from "@mui/icons-material";

const COLORS = [
  "#4caf50",
  "#2196f3",
  "#ff9800",
  "#9c27b0",
  "#e91e63",
  "#00bcd4",
];

export const CollaborationSection = ({
  selectedProperty,
  userToken,
  isBroker,
}: CollaborationSectionProps) => {
  const [openCollaborationModal, setOpenCollaborationModal] = useState(false);
  const [realtor, setRealtor] = useState({ id: 0 });
  const [formData, setFormData] = useState<CreateRealtorCollaborationRequest>({
    realtorId: 0,
    propertyId: 0,
    requestName: "",
    description: "",
    commissionPercentaje: "",
  });
  const [formErrors, setFormErrors] = useState({
    requestName: "",
    commissionPercentaje: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const [requests, setRequests] = useState<RealtorCollaborationRequest>(
    {} as RealtorCollaborationRequest
  );

  const [requestsForBrokerLead, setRequestsForBrokerLead] = useState<
    RealtorCollaborationRequest[]
  >([] as RealtorCollaborationRequest[]);

  const [selectedCommission, setSelectedCommission] = useState<number | null>(
    Number(formData.commissionPercentaje) || null
  );

  const navigate = useNavigate();

  const getAvailableCommissionOptions = useCallback(() => { 
  if (!selectedProperty?.commission) {
    return [50, 40, 20]; // Opciones por defecto si no hay términos especificados
  }

  const commissionText = selectedProperty.commission.toLowerCase().trim();
  const percentageRegex = /(\d+)%?/g;
  const matches = commissionText.match(percentageRegex);
  
  if (matches) {
    const percentages = matches
      .map((match: string) => parseInt(match.replace('%', ''), 10))
      .filter((num: number) => num > 0 && num <= 100)
      .sort((a: number, b: number) => b - a); // Ordenar de mayor a menor
    
    // Remover duplicados
    const uniquePercentages = [...new Set(percentages)];
    
    if (uniquePercentages.length > 0) {
      return uniquePercentages;
    }
  }
  
  // Si no se pueden extraer porcentajes válidos, usar opciones por defecto
  return [50, 40, 20];
}, [selectedProperty?.commission]);

  useEffect(() => {
    if (userToken && userToken.user) {
      const brokerProfile = userToken.user.profiles.find(
        (profile: { roleId: number; roleName: string }) =>
          profile.roleId === 3 && profile.roleName === "Broker"
      );
      if (brokerProfile) {
        setRealtor({ id: brokerProfile.realtor.id });
        setFormData((prev) => ({
          ...prev,
          realtorId: brokerProfile.realtor.id,
          propertyId: selectedProperty.id_Property,
        }));
        
        // Auto-seleccionar comisión si solo hay una opción disponible
        const availableOptions = getAvailableCommissionOptions() as any;
        if (availableOptions.length === 1) {
          setSelectedCommission(availableOptions[0]);
          setFormData((prev) => ({
            ...prev,
            commissionPercentaje: availableOptions[0].toString(),
          }));
        }
      }
    }
  }, [userToken, selectedProperty, getAvailableCommissionOptions]);

  const fetchCollaborationRequests = useCallback(async () => {
    try {
      const response = await getByRealtorAndPropertyId(
        realtor.id,
        selectedProperty.id_Property
      );
      if (response.success) {
        setRequests(response.data);
      } else {
        setSnackbar({
          open: true,
          message: "Error al cargar las solicitudes.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching collaboration requests:", error);
    }
  }, [realtor.id, selectedProperty.id_Property]);

  useEffect(() => {
    if (realtor.id && selectedProperty.id_Property) {
      fetchCollaborationRequests();
    }
  }, [realtor.id, fetchCollaborationRequests, selectedProperty.id_Property]);

  const fetchCollaborationRequestsForBrokerLead = useCallback(async () => {
    try {
      const response = await getByPropertyId(selectedProperty.id_Property);
      if (response.success) {
        setRequestsForBrokerLead(response.data);
      } else {
        setSnackbar({
          open: true,
          message: "Error al cargar las solicitudes.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error(
        "Error fetching collaboration requests for Broker Lead:",
        error
      );
    }
  }, [selectedProperty.id_Property]);

  useEffect(() => {
    if (selectedProperty.id_Property) {
      fetchCollaborationRequestsForBrokerLead();
    }
  }, [fetchCollaborationRequestsForBrokerLead, selectedProperty.id_Property]);

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let isValid = true;
    const errors = { requestName: "", commissionPercentaje: "" };
    if (!formData.requestName.trim()) {
      errors.requestName = "El nombre de la solicitud es requerido";
      isValid = false;
    }
    const commission = parseFloat(formData.commissionPercentaje);
    if (
      !commission ||
      isNaN(commission) ||
      commission <= 0 ||
      commission > 100
    ) {
      errors.commissionPercentaje =
        "La comisión debe ser un número entre 0 y 100";
      isValid = false;
    }
    setFormErrors(errors);
    return isValid;
  };
  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    try {
      const response = await createCollaborationRequest({
        propertyId: selectedProperty.id_Property,
        requestName: formData.requestName,
        description: formData.description,
        commissionPercentaje: parseFloat(formData.commissionPercentaje),
        realtorId: realtor.id,
      });
      if (response.success) {
        setSnackbar({
          open: true,
          message: "¡Solicitud enviada con éxito!",
          severity: "success",
        });
        await fetchCollaborationRequests();
        handleCollaborationModalClose();
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message, severity: "error" });
    }
  };

  const handleCollaborationModalOpen = () => setOpenCollaborationModal(true);
  const handleCollaborationModalClose = () => {
    setOpenCollaborationModal(false);
    setFormData({
      propertyId: 0,
      requestName: "",
      description: "",
      commissionPercentaje: "",
      realtorId: 0,
    });
    setFormErrors({ requestName: "", commissionPercentaje: "" });
  };

  const handleCommissionSelect = (value: number) => {
    setSelectedCommission(value);
    setFormData((prev) => ({
      ...prev,
      commissionPercentaje: value.toString(),
    }));
    setFormErrors((prev) => ({
      ...prev,
      commissionPercentaje: "",
    }));
  };

  const handleSnackbarClose = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  // const averageCommission = requests ? requests.commissionPercentaje : 0;

  const acceptedRequests = requestsForBrokerLead.filter(
    (request) => request.status === 1
  );

  const totalShared = acceptedRequests.reduce(
    (sum, r) => sum + r.commissionPercentaje,
    0
  );

  const pieData = [
    {
      name: "Tu comisión",
      value: 100 - totalShared,
    },
    ...acceptedRequests.map((r, index) => ({
      name: r.contact?.fullName || `Broker ${index + 1}`,
      value: r.commissionPercentaje,
    })),
  ];

  /////

  const acceptedRequestsForBrokers = requestsForBrokerLead.filter(
    (request) => request.status === 1
  );

  const totalSharedForBrokers = acceptedRequestsForBrokers.reduce(
    (sum, r) => sum + r.commissionPercentaje,
    0
  );

  const currentRealtorId = realtor?.id ?? null;

  const pieDataForBrokers: { name: string; value: number }[] = [];

  acceptedRequestsForBrokers.forEach((r, index) => {
    if (r.realtorId === currentRealtorId) {
      pieDataForBrokers.push({
        name: "Tu comisión",
        value: r.commissionPercentaje,
      });
    } else {
      pieDataForBrokers.push({
        name: r.contact?.fullName || `Broker ${index + 1}`,
        value: r.commissionPercentaje,
      });
    }
  });

  // Solo una vez fuera del forEach
  const remaining = 100 - totalSharedForBrokers;
  if (remaining > 0) {
    const ownerName =
      acceptedRequestsForBrokers[0]?.ownerBroker?.fullName;

    pieDataForBrokers.push({
      name: `${ownerName} (owner)`,
      value: remaining,
    });
  }

  const profile =
    userToken &&
    userToken.user.profiles.find(
      (profile: { roleId: number; roleName: string }) =>
        profile.roleId === 3 && profile.roleName === "Broker"
    );

  const commissionOptions = getAvailableCommissionOptions();

  // Función para obtener el tipo de comisión
  const getCommissionTypeText = (type: number) => {
    return type === 1 ? "Comisión Directa" : "Comisión Compartida";
  };

  // Función para obtener el icono del tipo de comisión
  const getCommissionTypeIcon = (type: number) => {
    return type === 1 ? <Handshake /> : <Share />;
  };

  return (
    <Box sx={{ mt: 2 }}>
      {requests.status !== 0 &&
        requests.status !== 1 &&
        profile &&
        profile.realtor?.id !== selectedProperty.id_Realtor && (
          <Button
            variant="contained"
            fullWidth
            onClick={handleCollaborationModalOpen}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 500,
              borderRadius: "12px",
              background: "linear-gradient(45deg, #4caf50, #2e7d32)",
              boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
              transition: "all 0.3s ease",
              mb: 2,
              "&:hover": {
                background: "linear-gradient(45deg, #43a047, #2e7d32)",
                boxShadow: "0 6px 25px rgba(76, 175, 80, 0.4)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Solicitar colaboración de {selectedProperty.modeDescription}
          </Button>
        )}

      <Dialog
        open={openCollaborationModal}
        onClose={handleCollaborationModalClose}
        TransitionComponent={Fade}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, textAlign: "center" }}>
          Hola, {userToken?.user?.fullName}, solicita la colaboración de{" "}
          {selectedProperty.modeDescription}.
        </DialogTitle>

        <DialogContent sx={{ px: 2, pb: 2 }}>
          {/* Información de comisión de la propiedad */}
          {(selectedProperty.commission && selectedProperty.commisionType) && (
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3, 
                mb: 3,
                background: "linear-gradient(135deg, rgba(76, 175, 80, 0.05), rgba(129, 199, 132, 0.05))",
                border: "1px solid rgba(76, 175, 80, 0.2)"
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Info sx={{ color: "#4caf50", mr: 1 }} />
                  <Typography variant="h6" sx={{ color: "#2e7d32", fontWeight: "bold" }}>
                    Información de Comisión de la Propiedad
                  </Typography>
                </Box>
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {selectedProperty.commisionType && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        icon={getCommissionTypeIcon(selectedProperty.commisionType)}
                        label={getCommissionTypeText(selectedProperty.commisionType)}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(76, 175, 80, 0.1)",
                          color: "#2e7d32",
                          fontWeight: "medium",
                          "& .MuiChip-icon": {
                            color: "#4caf50"
                          }
                        }}
                      />
                    </Box>
                  )}
                  
                  {selectedProperty.commission && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Percent sx={{ color: "#4caf50", fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: "medium" }}>
                        <strong>Términos:</strong> {selectedProperty.commission}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          <Card elevation={3} sx={{ borderRadius: 3 }}>
            <CardContent>
              <TextField
                label="Nombre de la solicitud"
                name="requestName"
                value={formData.requestName}
                onChange={handleFormChange}
                fullWidth
                margin="normal"
                size="small"
                error={!!formErrors.requestName}
                helperText={formErrors.requestName}
                required
                sx={{ borderRadius: 2 }}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />

              <TextField
                label="Descripción"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={4}
                margin="normal"
                size="small"
                sx={{ borderRadius: 2 }}
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />

              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Selecciona porcentaje de comisión:
                {commissionOptions.length === 1 && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                    (Solo una opción disponible según los términos de la propiedad)
                  </Typography>
                )}
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                {commissionOptions.map((value) => (
                  <Button
                    variant={
                      selectedCommission === value ? "contained" : "outlined"
                    }
                    color="success"
                    onClick={() => handleCommissionSelect(value as any)}
                    sx={{
                      borderRadius: 5,
                      textTransform: "none",
                      fontWeight: 600,
                      flex: commissionOptions.length <= 3 ? 1 : "0 1 auto",
                      minWidth: 80,
                      py: 1,
                      transition: "all 0.3s ease",
                      boxShadow:
                        selectedCommission === value
                          ? "0 4px 12px rgba(76, 175, 80, 0.3)"
                          : undefined,
                      "&:hover": {
                        backgroundColor:
                          selectedCommission === value
                            ? undefined
                            : "rgba(76,175,80,0.1)",
                      },
                    }}
                  >
                    {value as any}%
                  </Button>
                ))}
              </Box>

              {/* Mostrar información sobre los términos de comisión */}
              {selectedProperty.commission && (
                <Typography variant="caption" sx={{ mt: 1, color: "text.secondary", fontStyle: "italic" }}>
                  💡 Opciones basadas en los términos: "{selectedProperty.commission}"
                </Typography>
              )}

              {formErrors.commissionPercentaje && (
                <Typography variant="caption" color="error">
                  {formErrors.commissionPercentaje}
                </Typography>
              )}
            </CardContent>
          </Card>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleFormSubmit}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 500,
              borderRadius: "12px",
              background: "linear-gradient(45deg, #4caf50, #2e7d32)",
              boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(45deg, #43a047, #2e7d32)",
                boxShadow: "0 6px 25px rgba(76, 175, 80, 0.4)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Enviar Solicitud
          </Button>
        </DialogActions>
      </Dialog>

      {(requests.status === 0 || requests.status === 1) &&
        profile &&
        profile?.realtor?.id !== selectedProperty?.id_Realtor &&
        isBroker && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Comisión{" "}
              {requests.status === 0
                ? "propuesta"
                : requests.status === 1
                ? "asignada"
                : ""}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mt: 5, height: 250 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieDataForBrokers}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    dataKey="value"
                    labelLine={false}
                  >
                    {pieDataForBrokers.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value: number) => `${value}%`} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

      {acceptedRequests.length > 0 &&
        profile &&
        profile?.realtor?.id === selectedProperty?.id_Realtor && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Comisión total de solicitudes de colaboración aceptadas
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mt: 2, height: 250 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    dataKey="value"
                    labelLine={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value: number) => `${value}%`} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

      {acceptedRequests.length > 0 && (
        <Card
          elevation={3}
          sx={{
            borderRadius: 3,
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: 6,
            },
          }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Brokers colaboradores
            </Typography>

            {/* Información de comisión de la propiedad */}
            {(selectedProperty.commission || selectedProperty.commisionType) && (
              <Card 
                elevation={1} 
                sx={{ 
                  borderRadius: 2, 
                  mb: 3,
                  background: "linear-gradient(135deg, rgba(76, 175, 80, 0.03), rgba(129, 199, 132, 0.03))",
                  border: "1px solid rgba(76, 175, 80, 0.15)"
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: "#2e7d32", fontWeight: "bold", mb: 1.5 }}>
                    📋 Información de Comisión de la Propiedad
                  </Typography>
                  
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {selectedProperty.commisionType && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          icon={getCommissionTypeIcon(selectedProperty.commisionType)}
                          label={getCommissionTypeText(selectedProperty.commisionType)}
                          size="small"
                          variant="outlined"
                          sx={{
                            backgroundColor: "rgba(76, 175, 80, 0.08)",
                            borderColor: "rgba(76, 175, 80, 0.3)",
                            color: "#2e7d32",
                            fontWeight: "medium",
                            "& .MuiChip-icon": {
                              color: "#4caf50"
                            }
                          }}
                        />
                      </Box>
                    )}
                    
                    {selectedProperty.commission && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Percent sx={{ color: "#4caf50", fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                          <strong>Términos:</strong> {selectedProperty.commission}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {requestsForBrokerLead.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay brokers aún.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {requestsForBrokerLead.map((request, index) => (
                  <Grid item xs={12} key={index}>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid #eee",
                        borderRadius: 2,
                        backgroundColor: "#fafafa",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={500}>
                          {request.contact?.fullName}{" "}
                          {request.realtorId === realtor.id && "(Tú)"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Comisión: {request.commissionPercentaje}%
                        </Typography>
                      </Box>

                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        sx={{
                          borderRadius: 20,
                          textTransform: "none",
                          fontSize: "0.75rem",
                        }}
                        onClick={() =>
                          navigate(`/properties/realtor/${request.realtorId}`)
                        }
                      >
                        Ver perfil
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};